// FormEditor.js
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import FormTree from "./FormTree";
import FormPreviewPanel from "./FormPreviewPanel";
import FormMainPanel from "./FormMainPanel";
import * as Helpers from "./Helpers";
import { CONTROL_TYPE_OPTIONS } from "./Constants";
import { useFormMutations } from "./UseFormMutations";
import { useReorder } from "./UseReorder";
import "./FormEditor.css";
import PostAddIcon from "@mui/icons-material/PostAdd";

// Toggle for very chatty logs (per-render etc.)
const DEBUG_FORM_EDITOR_VERBOSE = false;

// Small form summary (still useful occasionally)
const summariseForm = (form) => {
  if (!form || !Array.isArray(form.pages)) {
    return { hasForm: false };
  }
  const pages = form.pages.length;
  const controlsPerPage = form.pages.map((p) =>
    Array.isArray(p?.controls) ? p.controls.length : 0
  );
  const totalControls = controlsPerPage.reduce((sum, n) => sum + n, 0);
  return { hasForm: true, pages, totalControls, controlsPerPage };
};

// Focused snapshot of a single control (for before/after + save logs)
const debugControlSnapshot = (form, pageIndex, controlIndex) => {
  if (!form || !Array.isArray(form.pages)) {
    return { exists: false };
  }

  const page = form.pages[pageIndex];
  const ctrl = page?.controls?.[controlIndex];
  if (!ctrl) {
    return { exists: false, pageIndex, controlIndex };
  }

  const options = Array.isArray(ctrl.options) ? ctrl.options : [];
  return {
    exists: true,
    pageIndex,
    controlIndex,
    key: ctrl.key,
    type: ctrl.type,
    required: !!ctrl.required,
    optionsLength: options.length,
    optionsPreview: options.slice(0, 5),
  };
};

export default function FormEditor({
  viewfieldsInstance,
  dictionaryResolvers, // { getPhraseSync(lang, key), getPhraseAsync(lang, key) }
  formPages,
  formLanguages,
  formName,
  formToken,
  onSaveForm,   // form save handler: onSaveForm(formObject)
  onSaveAssets, // asset save handler: onSaveAssets(updatedAssetsMap)
  superAdmin = false,
}) {
  /* --------------------- input validation & normalization --------------------- */

  if (DEBUG_FORM_EDITOR_VERBOSE) {
    console.log("FormEditor: initial render props", {
      viewfieldsInstance: !!viewfieldsInstance,
      formName,
      formToken,
      pagesCount: Array.isArray(formPages) ? formPages.length : 0,
    });
  }

  const hasValidPages = Array.isArray(formPages);

  const safeForm = useMemo(
    () => ({
      pages: hasValidPages ? Helpers.normalizePagesForEditor(formPages) : [],
    }),
    [hasValidPages, formPages]
  );

  if (DEBUG_FORM_EDITOR_VERBOSE) {
    console.log("FormEditor: safeForm baseline", summariseForm(safeForm));
  }

  /* -------------------------------- languages -------------------------------- */

  const resolvedLanguages = useMemo(() => {
    const explicit = Array.isArray(formLanguages) ? formLanguages : [];
    const norm = explicit.map(Helpers.normalizeLangCode).filter(Boolean);
    return norm.length ? norm : ["en"];
  }, [formLanguages]);

  /* ------------------------------ form + pending ------------------------------ */

  // The live form you see/edit in the UI
  const [workingForm, setWorkingForm] = useState(() => Helpers.clone(safeForm));

  // The latest unsaved form (null means "no pending form changes")
  const [updatedForm, setUpdatedForm] = useState(null);

  // Map of assetKey -> { content, type, language, name?, description? }
  // Empty object = no pending assets
  const [updatedAssets, setUpdatedAssets] = useState({});
  const [saveError, setSaveError] = useState("");

  // Always points at the latest updatedAssets (not a second cache)
  const updatedAssetsRef = useRef(updatedAssets);
  updatedAssetsRef.current = updatedAssets;

  // When the baseline form changes (e.g. parent loads/saves), reset working + pending
  useEffect(() => {
    if (DEBUG_FORM_EDITOR_VERBOSE) {
      console.log("FormEditor: safeForm changed, resetting working state", {
        safeForm: summariseForm(safeForm),
      });
    }
    setWorkingForm(Helpers.clone(safeForm));
    setUpdatedForm(null);
    setUpdatedAssets({});
    setSaveError("");
  }, [safeForm]);

  // Central helper for any structural form change
  // - Applies the change to workingForm
  // - Records the result as the latest pending form (updatedForm)
  const updateForm = useCallback((updaterOrNext) => {
    console.log("FormEditor: updateForm invoked", {
      updaterType: typeof updaterOrNext,
    });

    setWorkingForm((prev) => {
      const prevSummary = summariseForm(prev);
      const next =
        typeof updaterOrNext === "function"
          ? updaterOrNext(prev)
          : updaterOrNext ?? prev;
      const nextSummary = summariseForm(next);

      console.log("FormEditor: updateForm prev->next", {
        prev: prevSummary,
        next: nextSummary,
      });

      setUpdatedForm(next);
      return next;
    });
  }, []);

  // Helper to record an updated asset
  // - Each key only ever keeps the latest asset version
  // - Stores both content and metadata (type, language, name?, description?)
  const updateAsset = useCallback((assetKey, assetContent, meta) => {
    if (!assetKey) return;

    if (!meta || typeof meta !== "object") {
      throw new Error(
        `FormEditor: updateAsset("${assetKey}") requires meta object { type, language, name?, description? }`
      );
    }

    const { type, language, name, description } = meta;

    if (!type || typeof type !== "string") {
      throw new Error(
        `FormEditor: updateAsset("${assetKey}") missing required meta.type`
      );
    }

    if (!language || typeof language !== "string") {
      throw new Error(
        `FormEditor: updateAsset("${assetKey}") missing required meta.language`
      );
    }

    const nextEntry = {
      content: assetContent,
      type,
      language,
      ...(name ? { name } : {}),
      ...(description ? { description } : {}),
    };

    setUpdatedAssets((prev) => {
      const next = {
        ...prev,
        [assetKey]: nextEntry,
      };
      console.log("FormEditor: updateAsset", {
        assetKey,
        keys: Object.keys(next),
        entry: nextEntry,
      });
      return next;
    });
  }, []);

  /* --------------------------------- UI state -------------------------------- */

  const [lang, setLang] = useState(() => resolvedLanguages[0] || "en");
  useEffect(() => {
    if (!resolvedLanguages.includes(lang)) {
      setLang(resolvedLanguages[0] || "en");
    }
  }, [resolvedLanguages, lang]);

  const [selection, setSelection] = useState({ kind: "form" });

  const [editorMode, setEditorMode] = useState("content");
  useEffect(() => {
    if (selection?.kind !== "control" && editorMode === "attributes") {
      setEditorMode("content");
    }
  }, [selection, editorMode]);

  useEffect(() => {
    if (DEBUG_FORM_EDITOR_VERBOSE) {
      console.log("FormEditor: editorMode change", {
        to: editorMode,
      });
    }
  }, [editorMode]);

  // Log when updatedForm changes, with a snapshot of current selection if it’s a control
  useEffect(() => {
    if (!updatedForm) {
      if (DEBUG_FORM_EDITOR_VERBOSE) {
        console.log("FormEditor: updatedForm cleared");
      }
      return;
    }

    const selectedSnapshot =
      selection?.kind === "control"
        ? debugControlSnapshot(
            updatedForm,
            selection.pageIndex,
            selection.controlIndex
          )
        : null;

    console.log("FormEditor: updatedForm changed", {
      form: summariseForm(updatedForm),
      selection,
      selectedControl: selectedSnapshot,
    });
  }, [updatedForm, selection]);

  /* ------------------------------ derived values ----------------------------- */

  const controlTypeOptions = useMemo(
    () =>
      Helpers.buildControlTypeOptions(workingForm.pages, CONTROL_TYPE_OPTIONS),
    [workingForm.pages]
  );

  /* --------------------------- mutations & reordering ------------------------- */

  // All these helpers ultimately call updateForm(...) to change the form
  const {
    handleChangePhrase,
    handleChangeAttributes,
    handleSetAttributes,
    handleSetControlType,
    handleRenamePageTitle,
    handleRenameControlKey,
  } = useFormMutations(updateForm);

  const { handleMovePage, handleMoveControl } = useReorder(
    updateForm,
    setSelection
  );

  /* ----------------------------- JSON editor state ---------------------------- */

  const [formJsonText, setFormJsonText] = useState("");
  const [formJsonError, setFormJsonError] = useState("");
  const [formJsonDirty, setFormJsonDirty] = useState(false);

  useEffect(() => {
    if (editorMode !== "form") return;
    if (formJsonDirty) return;
    const pretty = JSON.stringify({ pages: workingForm.pages }, null, 2);
    setFormJsonText(pretty);
    setFormJsonError("");
  }, [editorMode, workingForm.pages, formJsonDirty]);

  const onFormJsonChange = useCallback((text) => {
    setFormJsonText(text);
    setFormJsonDirty(true);
    try {
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("Root must be an object with `pages`.");
      }
      if (parsed.pages && !Array.isArray(parsed.pages)) {
        throw new Error("`pages` must be an array.");
      }
      setFormJsonError("");
    } catch (e) {
      setFormJsonError(e.message || "Invalid JSON.");
    }
  }, []);

  /* -------------------------------- add/delete -------------------------------- */

  const handleAddPage = useCallback(() => {
    updateForm((prev) => {
      const next = Helpers.clone(prev);
      const pages = Array.isArray(next.pages) ? [...next.pages] : [];
      const insertAt = Math.max(0, pages.length - 1);
      const title = Helpers.uniquePageTitle(pages, "New Page");
      const newPage = {
        title,
        controls: [],
        navigation: [
          { active: true, label: "Cancel", type: "CANCEL" },
          { active: true, label: "Submit", type: "DONE" },
        ],
      };
      pages.splice(insertAt, 0, newPage);
      next.pages = pages;
      return next;
    });

    setSelection((sel) => {
      const total = workingForm.pages?.length || 0;
      const insertedAt = Math.max(0, total - 1);
      return { kind: "page", pageIndex: insertedAt };
    });
  }, [workingForm.pages, updateForm]);

  const handleDeletePage = useCallback(
    (pageIndex) => {
      if (!window.confirm("Delete this page?")) return;
      updateForm((prev) => {
        const next = Helpers.clone(prev);
        const arr = Array.isArray(next.pages) ? [...next.pages] : [];
        if (!Helpers.inBounds(pageIndex, arr.length)) return prev;
        arr.splice(pageIndex, 1);
        next.pages = arr;
        return next;
      });
      setSelection((sel) => {
        if (sel?.kind === "page") {
          if (sel.pageIndex === pageIndex) return { kind: "form" };
          if (sel.pageIndex > pageIndex)
            return { ...sel, pageIndex: sel.pageIndex - 1 };
        }
        if (sel?.kind === "control") {
          if (sel.pageIndex === pageIndex) return { kind: "form" };
          if (sel.pageIndex > pageIndex)
            return { ...sel, pageIndex: sel.pageIndex - 1 };
        }
        return sel;
      });
    },
    [updateForm]
  );

  const handleAddControl = useCallback(
    (pageIndex, afterControlIndexOrNull) => {
      const page = workingForm.pages?.[pageIndex];
      if (!page) return;
      const existing = Array.isArray(page.controls) ? page.controls : [];
      const insertAt =
        afterControlIndexOrNull == null ||
        afterControlIndexOrNull < 0 ||
        afterControlIndexOrNull >= existing.length
          ? existing.length
          : afterControlIndexOrNull + 1;

      const key = Helpers.uniqueControlKey(existing, "new_control");
      const newCtrl = {
        key,
        type: "TEXT",
        attributes: { height: 4, maxWords: 500 },
        required: false,
      };

      updateForm((prev) => {
        const next = Helpers.clone(prev);
        const p = next.pages?.[pageIndex];
        if (!p) return prev;
        const controls = Array.isArray(p.controls) ? [...p.controls] : [];
        controls.splice(insertAt, 0, newCtrl);
        next.pages[pageIndex] = { ...p, controls };
        return next;
      });

      setSelection({ kind: "control", pageIndex, controlIndex: insertAt });
    },
    [workingForm.pages, updateForm]
  );

  const handleDeleteControl = useCallback(
    (pageIndex, controlIndex) => {
      if (!window.confirm("Delete this element?")) return;
      updateForm((prev) => {
        const next = Helpers.clone(prev);
        const p = next.pages?.[pageIndex];
        if (!p || !Array.isArray(p.controls)) return prev;
        const arr = [...p.controls];
        if (!Helpers.inBounds(controlIndex, arr.length)) return prev;
        arr.splice(controlIndex, 1);
        next.pages[pageIndex] = { ...p, controls: arr };
        return next;
      });
      setSelection((sel) => {
        if (sel?.kind !== "control") return sel;
        if (sel.pageIndex !== pageIndex) return sel;
        if (sel.controlIndex === controlIndex)
          return { kind: "page", pageIndex };
        if (sel.controlIndex > controlIndex) {
          return { ...sel, controlIndex: sel.controlIndex - 1 };
        }
        return sel;
      });
    },
    [updateForm]
  );

  const handleSetOptions = useCallback(
    (pageIndex, controlIndex, nextOptions) => {
      console.log("FormEditor: onSetOptions (from FormMainPanel)", {
        pageIndex,
        controlIndex,
        nextOptionsType: typeof nextOptions,
        isArray: Array.isArray(nextOptions),
        nextOptionsPreview: Array.isArray(nextOptions)
          ? nextOptions.slice(0, 5)
          : nextOptions,
      });

      // Inline options are treated as form changes
      updateForm((prev) => {
        const beforeSnapshot = debugControlSnapshot(
          prev,
          pageIndex,
          controlIndex
        );
        console.log("FormEditor: handleSetOptions BEFORE", beforeSnapshot);

        const next = Helpers.updateControl(
          prev,
          pageIndex,
          controlIndex,
          (ctrl) => {
            if (
              typeof nextOptions === "string" ||
              Array.isArray(nextOptions)
            ) {
              ctrl.options = nextOptions;
            }
          }
        );

        const afterSnapshot = debugControlSnapshot(
          next,
          pageIndex,
          controlIndex
        );
        console.log("FormEditor: handleSetOptions AFTER", afterSnapshot);

        return next;
      });
    },
    [updateForm]
  );

  // Export inline options to an asset + remove inline options from control
  const handleExportOptionsToAsset = useCallback(
    (pageIndex, controlIndex, assetKey, assetContent, meta) => {
      if (!assetKey || !assetContent) {
        console.log("FormEditor: handleExportOptionsToAsset invalid input", {
          assetKey,
          assetContentType: typeof assetContent,
        });
        return;
      }

      if (!meta || typeof meta !== "object") {
        throw new Error(
          `FormEditor: handleExportOptionsToAsset("${assetKey}") requires meta object`
        );
      }

      const { type, language, name, description } = meta;

      if (!type || typeof type !== "string") {
        throw new Error(
          `FormEditor: handleExportOptionsToAsset("${assetKey}") missing required meta.type`
        );
      }

      if (!language || typeof language !== "string") {
        throw new Error(
          `FormEditor: handleExportOptionsToAsset("${assetKey}") missing required meta.language`
        );
      }

      console.log("FormEditor: handleExportOptionsToAsset", {
        pageIndex,
        controlIndex,
        assetKey,
        optionsCount: Array.isArray(assetContent) ? assetContent.length : 0,
        type,
        language,
      });

      // 1) Stage the asset for saveAssetsBatch(...)
      updateAsset(assetKey, assetContent, {
        type,
        language,
        name,
        description,
      });

      // 2) Remove inline options from the control to force asset-backed path
      updateForm((prev) =>
        Helpers.updateControl(prev, pageIndex, controlIndex, (ctrl) => {
          if ("options" in ctrl) {
            delete ctrl.options;
          }
        })
      );
    },
    [updateAsset, updateForm]
  );

  /* ------------------------------ preview title ------------------------------ */

  const previewTitle = useMemo(() => {
    const getSync = dictionaryResolvers?.getPhraseSync;
    if (selection.kind === "form") return "Form Preview";
    if (selection.kind === "page") {
      const p = workingForm.pages?.[selection.pageIndex];
      const resolved =
        (typeof getSync === "function" && p?.title
          ? getSync(lang, p.title)
          : null) || p?.title;
      return `Page Preview: ${
        resolved || `#${(selection.pageIndex ?? 0) + 1}`
      }`;
    }
    const page = workingForm.pages?.[selection.pageIndex];
    const ctrl = page?.controls?.[selection.controlIndex];
    return `Control Preview: ${ctrl?.key ?? "Control"}`;
  }, [selection, workingForm, lang, dictionaryResolvers]);

  /* -------------------------------- revert/save ------------------------------ */

  const handleRevert = useCallback(() => {
    // Reset to baseline form and drop all pending changes
    console.log("FormEditor: handleRevert invoked");
    setWorkingForm(Helpers.clone(safeForm));
    setUpdatedForm(null);
    setUpdatedAssets({});
    setSaveError("");

    if (editorMode === "form") {
      const pretty = JSON.stringify({ pages: safeForm.pages }, null, 2);
      setFormJsonText(pretty);
      setFormJsonError("");
      setFormJsonDirty(false);
    }
  }, [safeForm, editorMode]);

  const handleSave = useCallback(async () => {
    const getAssetsSnapshot = () => {
      const src = updatedAssetsRef.current;
      const validated =
        src && typeof src === "object" && !Array.isArray(src) ? src : {};
      const snap = { ...validated };
      const keys = Object.keys(snap);
      return { snap, keys, hasAny: keys.length > 0 };
    };

    const clearSavedAssetKeys = (assetKeysToSave, assetsToSave) => {
      if (!Array.isArray(assetKeysToSave) || assetKeysToSave.length === 0) return;

      setUpdatedAssets((prev) => {
        if (!prev || typeof prev !== "object" || Array.isArray(prev)) return prev;

        const next = { ...prev };
        for (const k of assetKeysToSave) {
          // delete only if unchanged since snapshot
          if (prev[k] === assetsToSave[k]) {
            delete next[k];
          }
        }
        return next;
      });
    };

    const saveAssetsIfAny = async () => {
      const { snap: assetsToSave, keys: assetKeysToSave, hasAny } =
        getAssetsSnapshot();

      if (!hasAny) return { ok: true, saved: false };

      if (typeof onSaveAssets !== "function") {
        console.warn(
          "FormEditor: pending assets but onSaveAssets is not a function",
          { assetKeys: assetKeysToSave }
        );
        setSaveError("Cannot save assets: asset save handler is not wired.");
        return { ok: false, saved: false };
      }

      try {
        const okAssets =
          (await Promise.resolve(onSaveAssets(assetsToSave))) !== false;

        if (!okAssets) {
          setSaveError("Assets failed to save. Please retry Save.");
          return { ok: false, saved: false };
        }

        clearSavedAssetKeys(assetKeysToSave, assetsToSave);
        return { ok: true, saved: true };
      } catch (err) {
        console.error("FormEditor: assets save threw", err);
        setSaveError("Assets failed to save. Please retry Save.");
        return { ok: false, saved: false };
      }
    };

    // JSON editor mode
    if (editorMode === "form") {
      let nextForm;
      try {
        const parsed = JSON.parse(formJsonText);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          setFormJsonError("Root must be an object with `pages`.");
          return;
        }
        const nextPagesRaw = parsed.pages ?? [];
        if (!Array.isArray(nextPagesRaw)) {
          setFormJsonError("`pages` must be an array.");
          return;
        }
        const nextPages = Helpers.normalizePagesForEditor(nextPagesRaw);
        nextForm = { pages: nextPages };
      } catch (err) {
        setFormJsonError(`Invalid JSON: ${err?.message || String(err)}`);
        return;
      }

      let okForm = true;
      try {
        okForm = (await Promise.resolve(onSaveForm?.(nextForm))) !== false;
      } catch (err) {
        console.error("FormEditor: form save threw (JSON editor mode)", err);
        okForm = false;
      }

      if (!okForm) {
        setSaveError("Form failed to save. Please retry Save.");
        return;
      }

      // 3A: commit form once persisted
      setWorkingForm(nextForm);
      setUpdatedForm(null);
      setFormJsonDirty(false);
      setFormJsonError("");
      setSaveError("");

      // Always attempt assets save (fresh snapshot at call time)
      const assetsRes = await saveAssetsIfAny();
      if (assetsRes.ok) setSaveError("");

      return;
    }

    // Normal mode
    if (updatedForm) {
      const selectedSnapshot =
        selection?.kind === "control"
          ? debugControlSnapshot(
              updatedForm,
              selection.pageIndex,
              selection.controlIndex
            )
          : null;

      console.log("FormEditor: handleSave (normal) saving updatedForm", {
        form: summariseForm(updatedForm),
        selection,
        selectedControl: selectedSnapshot,
      });

      let okForm = true;
      try {
        okForm = (await Promise.resolve(onSaveForm?.(updatedForm))) !== false;
      } catch (err) {
        console.error("FormEditor: form save threw (normal mode)", err);
        okForm = false;
      }

      if (!okForm) {
        setSaveError("Form failed to save. Please retry Save.");
        return;
      }

      setUpdatedForm(null);
      setSaveError("");
    }

    // Always attempt assets save (fresh snapshot at call time)
    const assetsRes = await saveAssetsIfAny();
    if (assetsRes.ok) setSaveError("");
  }, [
    editorMode,
    formJsonText,
    updatedForm,
    onSaveForm,
    onSaveAssets,
    selection,
    setUpdatedAssets,
    setWorkingForm,
    setUpdatedForm,
    setFormJsonDirty,
    setFormJsonError,
    setSaveError,
  ]);

  const hidePreview = editorMode === "attributes" || editorMode === "form";

  const hasJsonError = editorMode === "form" && !!formJsonError;

  // Derive "dirty" purely from pending objects and JSON staging
  const hasFormChanges =
    !!updatedForm || (editorMode === "form" && formJsonDirty);
  const hasAssetChanges = Object.keys(updatedAssets).length > 0;

  let toolbarStatus = "No changes";
  if (saveError) {
    toolbarStatus = saveError;
  } else if (hasJsonError) {
    toolbarStatus = `JSON error: ${formJsonError}`;
  } else if (hasFormChanges && hasAssetChanges) {
    toolbarStatus = "Unsaved form and asset changes";
  } else if (hasFormChanges) {
    toolbarStatus = "Unsaved form changes";
  } else if (hasAssetChanges) {
    toolbarStatus = "Unsaved asset changes";
  }

  const disableRevert = !hasFormChanges && !hasAssetChanges;
  const disableSave =
    (!hasFormChanges && !hasAssetChanges) || hasJsonError;

  if (DEBUG_FORM_EDITOR_VERBOSE) {
    console.log("FormEditor: toolbar state", {
      toolbarStatus,
      hasFormChanges,
      hasAssetChanges,
      disableRevert,
      disableSave,
    });
  }

  /* ---------------------------------- render --------------------------------- */

  return (
    <div className="fe-root">
      {/* Sidebar */}
      <aside className="fe-sidebar" aria-label="Form Structure">
        <div className="fe-sidebar__header">
          <div
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <div className="fe-title">Form Editor</div>
            <button
              className="fe-icon-btn"
              title="Add page (inserts before the last page)"
              onClick={handleAddPage}
              aria-label="Add page"
            >
              <PostAddIcon fontSize="small" />
            </button>
          </div>
          <div className="fe-count">
            {(workingForm.pages || []).length} pages
          </div>
        </div>

        <FormTree
          pages={workingForm.pages}
          formName={formName}
          formToken={formToken}
          lang={lang}
          selection={selection}
          onSelectForm={() => setSelection({ kind: "form" })}
          onSelectPage={(pi) => {
            setSelection({ kind: "page", pageIndex: pi });
            if (editorMode === "attributes") setEditorMode("content");
          }}
          onSelectControl={(pi, ci) =>
            setSelection({ kind: "control", pageIndex: pi, controlIndex: ci })
          }
          onRenamePageTitle={handleRenamePageTitle}
          onRenameControlKey={handleRenameControlKey}
          onAddControl={handleAddControl}
          onDeletePage={handleDeletePage}
          onDeleteControl={handleDeleteControl}
          onMovePage={handleMovePage}
          onMoveControl={handleMoveControl}
          dictionaryResolvers={dictionaryResolvers}
        />
      </aside>

      {/* Main column */}
      <main className={`fe-main ${hidePreview ? "is-attr-mode" : ""}`}>
        {/* Toolbar */}
        <div className="fe-toolbar">
          <div className="fe-tabs" role="tablist" aria-label="Languages">
            {resolvedLanguages.map((l) => (
              <button
                key={l}
                role="tab"
                aria-selected={l === lang}
                className={`fe-tab ${l === lang ? "is-active" : ""}`}
                onClick={() => setLang(l)}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="fe-actions">
            <div className="fe-actions__status">{toolbarStatus}</div>
            <button
              className="fe-btn fe-btn--ghost"
              onClick={handleRevert}
              disabled={disableRevert}
            >
              Revert
            </button>
            <button
              className="fe-btn fe-btn--primary"
              onClick={handleSave}
              disabled={disableSave}
            >
              Save
            </button>
          </div>
        </div>

        {/* Editor area */}
        <section className="fe-editor">
          <FormMainPanel
            selection={selection}
            lang={lang}
            pages={workingForm.pages}
            languages={resolvedLanguages}
            editorMode={editorMode}
            onEditorModeChange={setEditorMode}
            // dictionary edits (to be re-routed to updatedAssets later)
            onChangePhrase={handleChangePhrase}
            // attributes / control-level patches go through form helpers
            onChangeAttributes={(pageIndex, controlIndex, patch) => {
              handleChangeAttributes(pageIndex, controlIndex, patch);
            }}
            onSetAttributes={(nextObj, pageIndex, controlIndex) => {
              handleSetAttributes(pageIndex, controlIndex, nextObj);
            }}
            onSetOptions={(nextOptions, pageIndex, controlIndex) => {
              handleSetOptions(pageIndex, controlIndex, nextOptions);
            }}
            controlTypeOptions={controlTypeOptions}
            onChangeControlType={(nextType) => {
              if (selection?.kind === "control") {
                handleSetControlType(
                  selection.pageIndex,
                  selection.controlIndex,
                  nextType
                );
              }
            }}
            superAdmin={superAdmin}
            formJsonText={formJsonText}
            formJsonError={formJsonError}
            onFormJsonChange={onFormJsonChange}
            dictionaryResolvers={dictionaryResolvers}
            viewfieldsInstance={viewfieldsInstance}
            // future: child components can call these to push fully-built updates
            onFormUpdate={(nextForm) => {
              if (!nextForm) return;
              console.log("FormEditor: onFormUpdate from child", {
                form: summariseForm(nextForm),
              });
              setWorkingForm(nextForm);
              setUpdatedForm(nextForm);
            }}
            onAssetUpdate={(assetKey, assetObj, meta) => {
              updateAsset(assetKey, assetObj, meta);
            }}
            onExportOptionsToAsset={handleExportOptionsToAsset}
          />
        </section>

        {!hidePreview && (
          <section className="fe-preview">
            <div className="fe-preview__header">{previewTitle}</div>
            <FormPreviewPanel
              viewfieldsInstance={viewfieldsInstance}
              selection={selection}
              lang={lang}
              pages={workingForm.pages}
              dictionaryResolvers={dictionaryResolvers}
            />
          </section>
        )}
      </main>
    </div>
  );
}
